import React, { useState, useEffect, useCallback } from 'react';

import { BdsSidebar, BdsSkeleton } from 'blip-ds/dist/blip-ds-react';
import { Document } from '../../../../../models/knowledgeBase/Document';
import { KnowledgeBaseApi } from '../../../../../api/KnowledgeBaseApi';
import { useContentLocalizer } from '../../../../../hooks/useContentLocalizer';
import ReactMarkdown from 'react-markdown';
import { MarkdownContent } from './styles';
import PageHeader from 'components/PageHeader';
import { localization } from './localization';

interface UrlPathDetailSidebarProps {
  isOpen: boolean;
  urlPath: Document;
  tenantId: string;
  onDelete: (version: Document) => Promise<void>;
  onTitleEdit: (version: Document, newTitle: string) => Promise<void>;
  onClose: () => void;
}

const UrlPathDetailSidebar: React.FC<UrlPathDetailSidebarProps> = ({
  isOpen,
  tenantId,
  urlPath,
  onDelete,
  onTitleEdit,
  onClose,
}) => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
  const [contentError, setContentError] = useState<string | null>(null);

  const translate = useContentLocalizer(localization);

  const handleTitleEdit = useCallback(
    async (newTitle: string) => {
      await onTitleEdit(urlPath, newTitle);
    },
    [onTitleEdit, urlPath],
  );

  useEffect(() => {
    const fetchMarkdownContent = async () => {
      if (!isOpen || !urlPath.version_id) {
        return;
      }

      setIsLoadingContent(true);
      setContentError(null);

      try {
        const content = await KnowledgeBaseApi.getDocumentVersionContentAsync(
          tenantId,
          urlPath.catalog_id,
          urlPath.id,
          urlPath.version_id,
          'md',
        );
        setMarkdownContent(content);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching markdown content:', error);
        setContentError(translate.errors.failedToLoadContent);
        // Fallback to existing markdown_content if available
        setMarkdownContent(urlPath.markdown_content || '');
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchMarkdownContent();
  }, [isOpen, urlPath.id, urlPath.version_id, urlPath.catalog_id, tenantId, translate.errors.failedToLoadContent]);

  const renderContent = () => {
    if (isLoadingContent) {
      return (
        <div style={{ padding: '16px' }}>
          <BdsSkeleton width="100%" height="20px" />
          <BdsSkeleton width="90%" height="20px" />
          <BdsSkeleton width="95%" height="20px" />
          <BdsSkeleton width="85%" height="20px" />
        </div>
      );
    }

    if (contentError && !markdownContent) {
      return (
        <bds-grid padding="2">
          <bds-typo>{contentError}</bds-typo>
        </bds-grid>
      );
    }

    return <ReactMarkdown>{markdownContent}</ReactMarkdown>;
  };

  return (
    <BdsSidebar
      isOpen={isOpen}
      width={761}
      sidebarPosition="right"
      background="surface-1"
      onBdsToggle={(event: CustomEvent) => {
        if (!event.detail.value) {
          onClose();
        }
      }}
    >
      <div slot="header">
        <PageHeader title={urlPath.name} subtitle={urlPath.url} isEditable={true} onTitleEdit={handleTitleEdit} />
      </div>
      <div slot="body">
        <MarkdownContent>{renderContent()}</MarkdownContent>
      </div>
      <div slot="footer">
        <bds-button variant="outline" color="negative" onClick={() => onDelete(urlPath)}>
          {translate.actions.delete}
        </bds-button>
      </div>
    </BdsSidebar>
  );
};

export default UrlPathDetailSidebar;
